import { Font, Indicator, Modal } from 'is24-corecss';
import type { FC, PropsWithChildren } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useBodyScroll } from '@/customHooks/useBodyScroll.hook';
import { useElementDarkMode } from '@/pages/tenant/hooks/shared/useElementDarkMode';
import styles from './ADACUpsellModal.styles';
import { ADACLogo } from '@/components/content/benefits/modals/legalInsurance/components/ADACLogo';
import type { PlanType } from '@/components/content/welcomeModal/adacUpsell/model';
import mockADACBenefitsData from '@/components/content/welcomeModal/adacUpsell/mock';
import { Benefits } from '@/components/content/welcomeModal/adacUpsell/Benefits';
import { ProductTiles } from '@/components/content/welcomeModal/adacUpsell/ProductTiles';
import { useModalFocus } from '@/customHooks/useModalFocus.hook';
import { useHasMeinPlus } from '@/customHooks/useHasMeinPlus.hook';
import { report } from '@/tracking/tracking';
import { TrackingEvents } from '@/tracking/trackingEvents';

interface ADACUpsellModalProps {
  visible: boolean;
  onDismiss: () => void;
}

export const ADACUpsellModal = ({ visible, onDismiss }: ADACUpsellModalProps) => {
  const { t } = useTranslation();
  const isMeinPlus = useHasMeinPlus();
  const hasReportedPageView = useRef(false);
  const { data } = useADACUpsellModalData();
  const [selectedProduct, setSelectedProduct] = useState<PlanType>(data.defaultPlan);
  const titleId = useId();

  useBodyScroll(visible);
  useModalFocus(visible);
  useElementDarkMode('[data-testid="modal-backdrop"]', visible);

  useEffect(() => {
    setSelectedProduct(data.defaultPlan);
  }, [data.defaultPlan]);

  useEffect(() => {
    if (!visible) {
      hasReportedPageView.current = false;
      return;
    }
    if (!hasReportedPageView.current) {
      hasReportedPageView.current = true;
      report(TrackingEvents.ADAC_UPSELL.PAGE_VISITED, isMeinPlus);
    }
  }, [visible, isMeinPlus]);

  const learnMore = () => {
    report({ ...TrackingEvents.ADAC_UPSELL.LEARN_MORE_CLICKED, parameter2: selectedProduct }, isMeinPlus);
    // FIXME: Open the discounted Wohnen+ ADAC Quick Cart.
  };
  const continueToPayment = () => {
    report({ ...TrackingEvents.ADAC_UPSELL.CONTINUE_TO_PAYMENT_CLICKED, parameter2: selectedProduct }, isMeinPlus);
    // FIXME: Open the Wohnen+ ADAC Checkout with the selected billing interval.
  };

  const dismiss = () => {
    report(TrackingEvents.ADAC_UPSELL.MODAL_CLOSE, isMeinPlus);
    onDismiss();
  };

  const onProductSelect = (productId: PlanType) => {
    if (productId === selectedProduct) return;
    report({ ...TrackingEvents.ADAC_UPSELL.OPTION_CLICKED, parameter2: `option_adac_${productId}` }, isMeinPlus);
    setSelectedProduct(productId);
  };

  return (
    <Modal.Root isOpen={visible} onClose={dismiss}>
      <Modal.Backdrop>
        <Modal.Surface aria-labelledby={titleId}>
          <Modal.Header>
            <ADACTitle id={titleId}>
              <TransWithIndicator i18nKey='common.adacModal.product' />
            </ADACTitle>
            <Modal.CloseButton aria-label={t('common.buttons.close')} />
          </Modal.Header>
          <Body>
            <div>
              <Font variant='font-heading-medium-bold'>{t('common.adacModal.headline')}</Font>
              <p className={`${styles.benefitDescription} margin-none`}>
                <TransWithIndicator i18nKey='common.adacModal.discount-description' />
              </p>
            </div>
            <p className={`${styles.benefitDescription} margin-none`}>{t('common.adacModal.description')}</p>
            <Benefits className='margin-bottom-m' />
            <ProductTiles
              plans={data.plans}
              selectedProduct={selectedProduct}
              onProuctSelect={onProductSelect}
              className='margin-bottom-l'
            />
          </Body>

          <Modal.Footer>
            <Modal.SecondaryFooterButton label={t('common.adacModal.actions.secondary')} onClick={learnMore} />
            <Modal.FooterButton
              label={t('common.adacModal.actions.primary')}
              variant='filledBrand'
              onClick={continueToPayment}
            />
          </Modal.Footer>
        </Modal.Surface>
      </Modal.Backdrop>
    </Modal.Root>
  );
};

const ADACTitle: FC<PropsWithChildren<{ id: string }>> = ({ children, id }) => {
  const { t } = useTranslation();

  return (
    <Modal.Title id={id} className={styles.titleWrapper}>
      {children}
      <span className={styles.titleSeparator} aria-hidden='true' />
      <ADACLogo className={styles.adacLogo} />
      <Font variant='font-heading-xsmall-bold'>{t('common.adacModal.adac-product')}</Font>
    </Modal.Title>
  );
};

const Body: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Modal.Body className={styles.contentWrapper}>
      <div className={`${styles.body} grid-flex flex-column`} data-testid={`wohnen-plus-benefit-modal-adac`}>
        {children}
      </div>
    </Modal.Body>
  );
};

const TransWithIndicator = ({ i18nKey }: { i18nKey: string }) => (
  <Trans i18nKey={i18nKey} components={{ Indicator: <Indicator asSpan appearance='plus' /> }} />
);

const useADACUpsellModalData = () => {
  return { data: mockADACBenefitsData };
};
